<template>
  <default>
    <div class="tw-w-full">
      <bread-crumb :breadcrumbs="breadcrumbs" />

      <div v-if="!loading && policy" class="tw-container tw-mx-auto motor-policy-page">
        <v-card flat class="motor-policy-card">
          <div v-if="!evaluationLoading">
            <div v-if="evaluation.status === 'NOT_APPLIED' && policy.coverCode !== 106" class="motor-policy-alert">
              <schedule-inspection :policy-number="policy.policyNo" :scheduled="false" @inspection-scheduled="updateEvaluation" />
            </div>
          </div>
          <div v-if="policy.toRenew" class="motor-policy-banner" role="alert">
            <v-icon small color="#C2410C">mdi-bell-ring-outline</v-icon>
            <span>Your policy is due for renewal.</span>
            <router-link
              :to="renewLocation(policy)"
              class="motor-policy-banner__action"
            >
              Renew policy
            </router-link>
          </div>

          <div class="motor-policy-hero">
            <div class="motor-policy-hero__icon">
              <v-icon size="40" color="#184679">mdi-car-outline</v-icon>
            </div>
            <div class="motor-policy-hero__content">
              <h1 class="motor-policy-hero__title">{{ coverTitle }}</h1>
              <p class="motor-policy-hero__vehicle">{{ vehicleLine }}</p>
              <p class="motor-policy-hero__policy-no">Policy No. {{ policy.policyNo }}</p>
            </div>
          </div>

          <div class="motor-policy-docs">
            <policy-documents-panel
              title="Policy documents"
              loading-message="Loading policy documents…"
              waiting-message="Documents are being prepared. They are also sent by email when ready."
              :policy-number="policyNoForDocs"
              :documents="policyDocuments"
              :loading="policyDocLoading"
              embedded
              compact
              collapsible
              :initially-expanded="true"
              @retry="loadPolicyDocument"
            />
          </div>

          <div class="motor-policy-tabs">
            <v-tabs
                v-model="tab"
                background-color="transparent"
                color="primary"
                grow
            >
              <v-tab>Overview</v-tab>
              <v-tab>Benefit Details</v-tab>
              <v-tab>Insured Asset Details</v-tab>
            </v-tabs>
            <hr class="motor-policy-tabs__divider">

            <v-tabs-items v-model="tab">
              <v-tab-item>
                <div class="motor-policy-panel">
                  <div class="motor-stat-grid">
                    <div v-if="showApplicationRate" class="motor-stat-card">
                      <span class="motor-stat-label">Application rate</span>
                      <span class="motor-stat-value">{{ rateDisplayText }}</span>
                    </div>
                    <div class="motor-stat-card">
                      <span class="motor-stat-label">Start date</span>
                      <span class="motor-stat-value">{{ policy.wef | formatDate }}</span>
                    </div>
                    <div class="motor-stat-card">
                      <span class="motor-stat-label">Renewal date</span>
                      <span :class="['motor-stat-value', diffDate(policy.wet) ? 'motor-stat-value--danger' : '']">
                        {{ policy.renewalDate | formatDate }}
                      </span>
                    </div>
                    <div v-if="policySumInsured > 0" class="motor-stat-card">
                      <span class="motor-stat-label">Sum insured</span>
                      <span class="motor-stat-value">KES {{ policySumInsured | formatNum }}</span>
                    </div>
                    <div class="motor-stat-card motor-stat-card--premium">
                      <span class="motor-stat-label">Total annual premium</span>
                      <span class="motor-stat-value motor-stat-value--premium">KES {{ policy.totalPremium | formatNum }}</span>
                    </div>
                  </div>

                  <div class="motor-section">
                    <h2 class="motor-section__title">Installments</h2>
                    <div v-if="!scheduleLoading" class="motor-installments">
                      <div v-if="paymentSchedule.length > 0" class="motor-installments__list">
                        <div v-for="(schedule, $index) in paymentSchedule" :key="$index" class="motor-installment-row">
                          <span>{{ formatPercentage(schedule.distribution) }}% of total premium</span>
                          <span>
                            KES {{ schedule.amount | formatNum }}
                            <small :class="schedule.paymentStatus === 'PAID' ? 'motor-paid' : 'motor-due'">
                              {{ schedule.paymentStatus === 'PAID' ? 'PAID' : `due - ${formatDate(schedule.dueDate)}` }}
                            </small>
                          </span>
                        </div>
                      </div>
                      <div v-else class="motor-installments__list">
                        <div class="motor-installment-row">
                          <span>100% of total premium</span>
                          <span>
                            KES {{ policy.totalPremium | formatNum }}
                            <small class="motor-paid">PAID</small>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="!evaluationLoading && evaluation.status !== 'NOT_APPLIED'" class="motor-section">
                    <h2 class="motor-section__title">Inspection</h2>
                    <div class="motor-inspection-card">
                      <p>You have a scheduled inspection for <strong>{{ vehicleLine }}</strong></p>
                      <p><strong>Date:</strong> {{ evaluation.date | formatDate }}</p>
                      <p><strong>Time:</strong> {{ evaluation.time }} HRS</p>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="motor-policy-panel">
                  <div class="motor-section">
                    <h2 class="motor-section__title">Top &amp; Other Free Benefits</h2>
                    <p v-if="displayFreeBenefits.length === 0" class="motor-empty-hint">
                      Standard cover limits apply to this policy.
                    </p>
                    <div v-else class="motor-benefits-grid">
                      <div v-for="(b,i) in displayFreeBenefits" :key="`free-${b.code || i}`" class="motor-benefit-card">
                        <p class="motor-benefit-name">{{ b.apaNarration }}</p>
                        <p class="motor-benefit-limit">Free limit - KES {{ b.value }}</p>
                      </div>
                    </div>
                  </div>

                  <div v-if="policy.coverCode !== 106" class="motor-section">
                    <h2 class="motor-section__title">Purchased Benefits</h2>
                    <p v-if="displayPurchasedBenefits.length === 0" class="motor-empty-hint">
                      No add-on benefits on this policy.
                    </p>
                    <div v-else class="motor-benefits-grid motor-benefits-grid--purchased">
                      <div
                        v-for="(b, i) in displayPurchasedBenefits"
                        :key="`paid-${b.sectCode || b.code || i}`"
                        class="motor-benefit-card"
                      >
                        <p class="motor-benefit-name">{{ b.narration }}</p>
                        <p class="motor-benefit-limit">Premium - KES {{ b.chargedAmount | formatNum }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="motor-policy-panel">
                  <div v-for="(car, i) in displayRisks" :key="`${car.regno || i}-${car.covertype || ''}`" class="motor-asset-card">
                    <div class="motor-asset-card__hero">
                      <v-icon color="primary" size="56">mdi-car-outline</v-icon>
                      <div>
                        <h3 class="motor-asset-card__title">
                          {{ car.model }}<span v-if="riskYear(car)">, {{ riskYear(car) }}</span>
                        </h3>
                        <p class="motor-asset-card__cover">{{ car.covertype }}</p>
                      </div>
                    </div>
                    <div class="motor-asset-card__details">
                      <div class="motor-asset-detail">
                        <span>Registration number</span>
                        <strong>{{ car.regno }}</strong>
                      </div>
                      <div class="motor-asset-detail">
                        <span>Sum insured</span>
                        <strong>KES {{ riskSumInsured(car) | formatNum }}</strong>
                      </div>
                      <div class="motor-asset-detail">
                        <span>Premium</span>
                        <strong>KES {{ riskPremium(car) | formatNum }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-card>
      </div>
      <div v-else-if="!loading" class="tw-container tw-mx-auto motor-policy-page">
        <v-card flat class="motor-policy-card motor-policy-empty">
          <v-icon size="40" color="#94A3B8">mdi-file-search-outline</v-icon>
          <h1 class="motor-policy-empty__title">We couldn't open this policy</h1>
          <p class="motor-policy-empty__text">{{ loadError }}</p>
          <div class="motor-policy-empty__actions">
            <v-btn color="primary" depressed @click="loadPolicy">Try again</v-btn>
            <v-btn text color="primary" :to="{ name: 'MyPolicies' }">My Policies</v-btn>
          </div>
        </v-card>
      </div>
      <div v-else class="tw-w-full tw-flex tw-justify-center">
        <v-progress-circular
            :size="70"
            color="primary1"
            indeterminate
        ></v-progress-circular>
      </div>
    </div>
  </default>
</template>

<script>
/* eslint-disable no-param-reassign */

import Default from '@/components/layout/Default';
import BreadCrumb from '@/components/dashboard/BreadCrumb';
import PolicyDocumentsPanel from '@/components/pa/PolicyDocumentsPanel';
import moment from 'moment';
import helpers from '@/utils/helpers';
import ScheduleInspection from '@/components/buy/ScheduleInspection';
import { diffDate, diffDate2 } from '@/utils/time';
import {
  displayMotorFreeBenefits,
  displayMotorPurchasedBenefits,
  displayMotorRisks,
  normalizeMotorPolicyNoForDocs,
  resolveMotorRiskPremium,
  resolveMotorSumInsured,
} from '@/utils/motorPolicyDisplay';
import {
  motorCoverLabel,
  motorRiskYear,
  motorVehicleLine,
} from '@/utils/motorPolicyList';
import { fetchPolicyDocuments } from '@/utils/policyDocumentsApi';
import { customerPolicyRenewalLocation } from '@/utils/agentRenewal';

export default {
  name: 'SinglePolicy',
  components: {
    ScheduleInspection, BreadCrumb, Default, PolicyDocumentsPanel,
  },
  data(vm) {
    return {
      breadcrumbs: [
        {
          text: 'My Policies',
          disabled: false,
          to: { name: 'MyPolicies' },
        },
        {
          text: 'My Policy',
          disabled: true,
          to: { name: 'SinglePolicy', params: { policyNo: vm.$route.params.policyNo } },
        },
      ],
      tab: null,
      loading: true,
      scheduleLoading: false,
      evaluationLoading: true,
      evaluation: {},
      policyDocLoading: false,
      policyDocuments: null,
      loadError: '',
    };
  },
  computed: {
    paymentSchedule() {
      return Array.isArray(this.policy?.paymentSchedule) ? this.policy.paymentSchedule : [];
    },
    policy() {
      return this.$store.state.policy;
    },
    coverTitle() {
      return `${motorCoverLabel(this.policy?.coverType)} Insurance`;
    },
    vehicleLine() {
      return motorVehicleLine(this.policy);
    },
    showApplicationRate() {
      return this.policy?.rateApplied != null && this.policy.rateApplied !== '';
    },
    rateDisplayText() {
      if (!this.showApplicationRate) return '';
      if (this.policy.coverCode === 106) {
        return `KES ${this.policy.rateApplied}`;
      }
      return `${this.policy.rateApplied}%`;
    },
    displayRisks() {
      return displayMotorRisks(this.policy);
    },
    displayFreeBenefits() {
      return displayMotorFreeBenefits(this.policy?.benefits);
    },
    displayPurchasedBenefits() {
      return displayMotorPurchasedBenefits(this.policy?.chargedBenefits);
    },
    policySumInsured() {
      const primary = this.displayRisks[0];
      return resolveMotorSumInsured(this.policy, primary);
    },
    policyNoForDocs() {
      return normalizeMotorPolicyNoForDocs(this.policy?.policyNo || this.$route.params.policyNo);
    },
  },
  metaInfo: {
    title: 'My Policy',
  },
  mounted() {
    this.loadPolicy();
  },
  methods: {
    renewLocation: customerPolicyRenewalLocation,
    async loadPolicy() {
      this.loading = true;
      this.loadError = '';
      try {
        await this.$store.dispatch('getPolicy', this.$route.params.policyNo);
        if (!this.policy) {
          this.loadError = 'Policy details are not available yet. A newly issued or renewed policy can take a short while to appear.';
        }
      } catch {
        this.loadError = 'Something went wrong while loading this policy. Please try again.';
      } finally {
        this.loading = false;
      }
      if (this.policy) this.loadPolicyDocument();
    },
    async loadPolicyDocument() {
      if (!this.policyNoForDocs) return;
      this.policyDocLoading = true;
      try {
        const result = await fetchPolicyDocuments(this.policyNoForDocs);
        this.policyDocuments = result.success ? (result.documents || []) : [];
      } finally {
        this.policyDocLoading = false;
      }
    },
    // getEvaluation() {
    //   axios.get(`/customer/quotation/evaluation/status?policyNumber=${this.$route.params.policyNo}`)
    //     .then((response) => {
    //       this.evaluation = response.data;
    //       this.evaluationLoading = false;
    //     });
    // },
    // deleteSchedule() {
    //   // eslint-disable-next-line no-restricted-globals,no-alert
    //   if (confirm('Are you sure you want to cancel ?')) {
    //     axios.delete('/customer/quotation/evaluation/cancel', { data: { policyNumber: this.$route.params.policyNo } })
    //       .then(() => {
    //         this.$toast.success('Appointment Cancelled');
    //         this.evaluation.status = 'NOT_APPLIED';
    //       });
    //   }
    // },
    formatDate(date) {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      return moment(date, 'Do MMM YYYY').format('Do MMM YYYY');
    },
    formatPercentage(num) {
      num *= 100;
      num = num.toString();
      num = num.split('.');
      num = parseInt(num[0], 10);
      num /= 100;
      return num;
    },
    updateEvaluation(data) {
      this.evaluation = data;
    },
    riskYear(car) {
      return motorRiskYear(car, this.policy);
    },
    riskSumInsured(car) {
      return resolveMotorSumInsured(this.policy, car);
    },
    riskPremium(car) {
      return resolveMotorRiskPremium(this.policy, car);
    },
    diffDate,
    diffDate2,
  },
  filters: {
    formatDate(date) {
      return moment(date, 'YYYY-MM-DD').format('Do MMM YYYY');
    },
    formatNum(num) {
      return helpers.numberToString(num);
    },
  },
};
</script>

<style scoped>
.motor-policy-page {
  max-width: 960px;
  padding-bottom: 2rem;
}

.motor-policy-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.motor-policy-empty__title {
  margin: 4px 0 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #184679;
}

.motor-policy-empty__text {
  max-width: 420px;
  margin: 0;
  font-size: 0.9rem;
  color: #64748B;
}

.motor-policy-empty__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
}

.motor-policy-card {
  margin-top: 1rem;
  padding: 1rem 1rem 1.5rem;
  border: 1px solid #EEF2F6;
  border-radius: 16px;
  background: #fff;
}

@media (min-width: 768px) {
  .motor-policy-card {
    margin-top: 1.5rem;
    padding: 1.25rem 1.5rem 1.75rem;
  }
}

.motor-policy-alert,
.motor-policy-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 1rem;
  padding: 10px 14px;
  border-radius: 10px;
  background: #FFEDD5;
  color: #9A3412;
  font-size: 0.875rem;
  font-weight: 600;
}

.motor-policy-banner__action {
  margin-left: auto;
  color: #C2410C;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.motor-policy-hero {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #F8FAFC 0%, #EEF4FB 100%);
}

.motor-policy-hero__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(24, 70, 121, 0.08);
}

.motor-policy-hero__title {
  margin: 0;
  color: #1E4276;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.25;
}

.motor-policy-hero__vehicle {
  margin: 6px 0 0;
  color: #0284C7;
  font-size: 0.95rem;
  font-weight: 600;
}

.motor-policy-hero__policy-no {
  margin: 8px 0 0;
  color: #64748B;
  font-size: 0.875rem;
  font-weight: 600;
}

.motor-policy-docs {
  margin-bottom: 1.25rem;
}

.motor-policy-tabs__divider {
  margin: 0 0 1rem;
  border: 0;
  border-top: 1px solid #E2E8F0;
}

.motor-policy-panel {
  padding: 0.25rem 0 0.5rem;
}

.motor-stat-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 1.5rem;
}

@media (min-width: 640px) {
  .motor-stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.motor-stat-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  background: #F8FAFC;
}

.motor-stat-card--premium {
  grid-column: 1 / -1;
}

.motor-stat-label {
  color: #94A3B8;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.motor-stat-value {
  color: #1E4276;
  font-size: 0.95rem;
  font-weight: 700;
}

.motor-stat-value--premium {
  color: #184679;
  font-size: 1.35rem;
}

.motor-stat-value--danger {
  color: #DC2626;
}

.motor-section {
  margin-top: 1.5rem;
}

.motor-section__title {
  margin: 0 0 12px;
  color: #1E4276;
  font-size: 1rem;
  font-weight: 700;
}

.motor-installments__list {
  padding: 14px 16px;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  background: #F8FAFC;
}

.motor-installment-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  color: #334155;
  font-size: 0.9rem;
}

.motor-installment-row + .motor-installment-row {
  border-top: 1px solid #E2E8F0;
}

.motor-paid {
  color: #16A34A;
  font-weight: 700;
}

.motor-due {
  color: #DC2626;
  font-weight: 700;
}

.motor-inspection-card {
  padding: 14px 16px;
  border-radius: 12px;
  background: #FFEDD5;
  color: #7C2D12;
}

.motor-benefits-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

@media (min-width: 768px) {
  .motor-benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .motor-benefits-grid--purchased {
    grid-template-columns: repeat(3, 1fr);
  }
}

.motor-benefit-card {
  padding: 12px 14px;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  background: #F8FAFC;
}

.motor-empty-hint {
  margin: 0;
  color: #64748B;
  font-size: 0.875rem;
  line-height: 1.5;
}

.motor-benefit-name {
  margin: 0;
  color: #334155;
  font-size: 0.9rem;
  font-weight: 600;
}

.motor-benefit-limit {
  margin: 4px 0 0;
  color: #184679;
  font-size: 0.875rem;
  font-weight: 700;
}

.motor-asset-card {
  padding: 16px;
  border: 1px solid #EEF2F6;
  border-radius: 14px;
  background: #F8FAFC;
}

.motor-asset-card + .motor-asset-card {
  margin-top: 12px;
}

.motor-asset-card__hero {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 14px;
}

.motor-asset-card__title {
  margin: 0;
  color: #1E4276;
  font-size: 1.05rem;
  font-weight: 700;
}

.motor-asset-card__cover {
  margin: 4px 0 0;
  color: #64748B;
  font-size: 0.875rem;
}

.motor-asset-card__details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

@media (min-width: 640px) {
  .motor-asset-card__details {
    grid-template-columns: repeat(3, 1fr);
  }
}

.motor-asset-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #64748B;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.motor-asset-detail strong {
  color: #1E4276;
  font-size: 0.92rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
}
</style>
